import { bindServices } from '@/services/api';
import { getById } from '@/services/global';
import { CoreEnums } from '@/utils/domain/constants';
import { getFiltersGlobalFilters, toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { isEmpty, merge } from 'lodash';

const api = bindServices('delegationConfigurations');
const { delegationConfigurations } = CoreQueries;
const { BulkActionType } = CoreEnums;

const FETCH_ROUTE = 'delegation/config';

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

/**
 * Child of delegations — Pattern X (like subSequences): parent-scoped list via
 * `delegation/getConfig/:id`, not createCrudModel `/paged`.
 */
const Model = {
  namespace: 'delegationConfigurations',

  state: {
    list: [],
    orderBy: '',
    pagination: { ...meta },
    searchTable: '',
    selectedItem: {},
    openCreateOrEdit: false,
    detail: false,
    id: null,
  },

  effects: {
    *fetchList({ payload }, { call, put }) {
      try {
        const { readPriority, route, page, limit, search, orderBy, filter, ...rest } =
          payload || {};
        const response = yield call(api.getAll, {
          route: route || `${FETCH_ROUTE}/paged`,
          params: {
            page,
            limit,
            search,
            orderBy,
            filter,
            ...delegationConfigurations.paged,
            ...rest,
          },
          readPriority: readPriority ?? false,
        });
        yield put({
          type: 'queryList',
          payload: {
            ...response,
            page,
            limit,
            searchTable: search,
          },
        });
      } catch (error) {}
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'orderBy', payload });
    },

    *hideAllForms(_, { put }) {
      yield put({ type: 'hideForms' });
    },

    *deleteItem({ id, params }, { call, put }) {
      let shouldReload = false;
      try {
        yield call(api.remove, { route: FETCH_ROUTE, id });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        shouldReload = true;
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({
          type: 'global/modalConfirm',
          payload: { state: false, id: null, modelDelete: null },
        });
      }
      if (shouldReload) {
        yield put({ type: 'fetchList', payload: params });
      }
    },

    *toggleCreateOrEditDrawer({ payload: { id, state, item, detail } }, { put }) {
      yield put({
        type: 'queryToggleCreateOrEditDrawer',
        payload: { id, state, item, detail: !!detail },
      });
    },

    *saveItem({ payload }, { call, put }) {
      try {
        const params = { route: FETCH_ROUTE, data: payload.data };
        if (payload.data.id) {
          yield call(api.update, params);
        } else {
          yield call(api.create, params);
        }
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: payload.params });
        yield put({
          type: 'toggleCreateOrEditDrawer',
          payload: { id: null, state: false, item: null },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
              autoHide: true,
            },
            validationErrors: error.validationErrors,
          });
        }
      }
    },

    *getItem({ payload }, { call, put }) {
      try {
        if (payload?.item && !isEmpty(payload.item)) {
          yield put({
            type: 'queryGetItem',
            payload: { id: payload.id, item: payload.item },
          });
          return;
        }
        const response = yield call(getById, {
          route: FETCH_ROUTE,
          id: payload.id,
          params: { ...delegationConfigurations.paged },
        });
        yield put({
          type: 'queryGetItem',
          payload: { id: payload.id, item: response },
        });
      } catch (error) {}
    },

    *bulkDeleteDelegationConfigs({ payload }, { call, put, select }) {
      let reloadPayload = null;
      try {
        const { ids, idDelegation } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: FETCH_ROUTE,
          bulkActionType: BulkActionType.Delete,
          ids,
        });

        const globalState = yield select((state) => state.global);
        const configState = yield select((state) => state.delegationConfigurations);
        const extraFilters = getFiltersGlobalFilters(
          globalState.globalFilters,
          'delegationConfigurations',
        );

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        reloadPayload = {
          page: 1,
          route: `delegation/getConfig/${idDelegation}`,
          filter: extraFilters || null,
          search: configState.searchTable || null,
          orderBy: configState.orderBy,
          readPriority: true,
        };
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({
          type: 'global/modalConfirm',
          payload: { state: false, id: null, modelDelete: null },
        });
      }
      if (reloadPayload) {
        yield put({ type: 'fetchList', payload: reloadPayload });
      }
    },
  },

  reducers: {
    queryList(state, { payload }) {
      return {
        ...state,
        list: payload.items,
        pagination: merge(state.pagination, toPaginate(payload)),
        searchTable: payload.searchTable ? payload.searchTable : '',
      };
    },
    orderBy(state, { payload }) {
      return { ...state, orderBy: payload };
    },
    hideForms(state) {
      return { ...state, orderBy: '' };
    },
    queryToggleCreateOrEditDrawer(state, { payload }) {
      return {
        ...state,
        id: payload.id,
        openCreateOrEdit: payload.state,
        selectedItem: payload.item,
        detail: payload.detail ?? false,
      };
    },
    queryGetItem(state, { payload }) {
      return { ...state, selectedItem: payload.item };
    },
    setSearchTable(state, { payload }) {
      return { ...state, searchTable: payload ?? '' };
    },
    clearList(state) {
      return {
        ...state,
        list: [],
        pagination: { ...meta },
      };
    },
  },
};

export default Model;
